import { FC } from "react";

import Stack from "@mui/material/Stack";
import {
  AvailablePaymentCurrency,
  formatAddressForDisplay,
  getField,
  Locales,
  parseStructuredAddress,
  QUESTION_TYPES_WITH_CHOICES,
  QuestionType,
} from "@simplyk/common";

import { CommandFromQuery } from "../types";

import { AnswerSeparator } from "@/common/constants/CustomAnswers";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { isAnswerDisplayedInMemberRow } from "@/common/helpers/memberIdentityAnswers";
import { useTranslate } from "@/common/hooks/useTranslate";
import { FrontendFormOutput } from "@/common/types/trpc";
import { PaymentDetailCard } from "@/components/PaymentDetailCard/PaymentDetailCard";
import { DashboardDivider } from "@/dashboard-design-system/Divider/DashboardDivider";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { IconElement } from "@/icons/types";

type Rate = NonNullable<FrontendFormOutput["rates"]>[number];
type ProductTicket = NonNullable<CommandFromQuery["productTickets"]>[number];
type ProductTicketAnswer = NonNullable<ProductTicket["answers"]>[number];
type TicketMember = NonNullable<CommandFromQuery["ticketMembers"]>[number];

interface TicketsProps {
  productTickets: NonNullable<CommandFromQuery["productTickets"]>;
  ratesMap: Record<string, Rate>;
  currency: AvailablePaymentCurrency;
  membersByProductTicketId?: Record<string, TicketMember>;
  icon?: IconElement;
  isSweepstakes?: boolean;
}

export const Tickets: FC<TicketsProps> = ({
  productTickets,
  ratesMap,
  currency,
  membersByProductTicketId,
  icon,
  isSweepstakes = false,
}) => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();

  return (
    <Stack spacing={1.5}>
      <DashboardTypography variant="subtitle2" vibe="text-neutral-quiet" sx={{ textTransform: "uppercase" }}>
        {t(
          "dashboard",
          isSweepstakes ? "sweepstakes.entriesReceived" : "donorTransactionDetailDrawer.ticketsPurchased"
        )}
      </DashboardTypography>
      <Stack spacing={1.5}>
        {productTickets.map((productTicket, index) => {
          const title = getTicketTitle(productTicket.rateId, ratesMap, locale);
          const member = membersByProductTicketId?.[productTicket.id];
          const sortedAnswers = productTicket.answers
            ?.slice()
            .filter((answer) => !isAnswerDisplayedInMemberRow(answer.question?.type, member, answer.answer))
            .sort((a, b) => (a.question?.sortIndex ?? 0) - (b.question?.sortIndex ?? 0));

          return (
            <PaymentDetailCard
              key={productTicket.id}
              title={title}
              index={index + 1}
              amount={productTicket.amount}
              status={productTicket.status}
              paymentCurrency={currency}
              icon={icon}
            >
              {member && (
                <>
                  <DashboardDivider />
                  <Stack sx={{ pl: 6, py: 0.5 }}>
                    <DashboardTypography variant="subtitle2" vibe="text-neutral-moderate">
                      {t("dashboard", "donorTransactionDetailDrawer.member")}
                    </DashboardTypography>
                    <DashboardTypography
                      variant="body2"
                      vibe="text-neutral-moderate"
                      sx={{ wordBreak: "break-word" }}
                      data-sensitive="person-name"
                      data-test="donor-drawer-member"
                    >
                      {formatMemberName(member)}
                    </DashboardTypography>
                  </Stack>
                </>
              )}
              {sortedAnswers && sortedAnswers.length > 0 && (
                <Stack spacing={1.5}>
                  {sortedAnswers.map((answer) => (
                    <Stack key={answer.id} spacing={1.5}>
                      <DashboardDivider />
                      <Stack sx={{ pl: 6 }}>
                        <TicketAnswerDisplay answer={answer} />
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              )}
            </PaymentDetailCard>
          );
        })}
      </Stack>
    </Stack>
  );
};

const formatMemberName = (member: TicketMember): string => {
  const fullName = `${member.firstName} ${member.lastName}`.trim();
  return member.email ? `${fullName} (${member.email})` : fullName;
};

const TicketAnswerDisplay: FC<{ answer: ProductTicketAnswer }> = ({ answer }) => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();

  const question = answer.question;
  if (!question) {
    return null;
  }

  const questionLabel =
    question.type === QuestionType.Name
      ? t("dashboard", "common.name")
      : (getField(question.questionFields, locale)?.body ?? "");

  return (
    <Stack>
      <DashboardTypography variant="subtitle2" vibe="text-neutral-moderate">
        {questionLabel}
      </DashboardTypography>
      <DashboardTypography variant="body2" vibe="text-neutral-moderate">
        {getDisplayValue(answer, locale, t)}
      </DashboardTypography>
    </Stack>
  );
};

/**
 * For shop variants, `productTicket.rateId` is a child rate id whose own title is just the variant combination (e.g. "S, Red")
 */
const getTicketTitle = (rateId: string, ratesMap: Record<string, Rate>, locale: Locales): string => {
  const rate = ratesMap[rateId];
  const childTitle = (rate?.fields && getField(rate.fields, locale)?.title) || "";
  if (!rate?.parentRateId) {
    return childTitle;
  }
  const parent = ratesMap[rate.parentRateId];
  const parentTitle = (parent?.fields && getField(parent.fields, locale)?.title) || "";
  if (!parentTitle) {
    return childTitle;
  }
  if (!childTitle) {
    return parentTitle;
  }
  return `${parentTitle} — ${childTitle}`;
};

const getDisplayValue = (
  answer: ProductTicketAnswer,
  locale: Locales,
  t: ReturnType<typeof useTranslate>["t"]
): string => {
  const questionType = answer.question?.type;

  if (questionType && QUESTION_TYPES_WITH_CHOICES.includes(questionType)) {
    const choiceLabels = answer.choices?.map((choice) => getField(choice.choiceFields, locale)?.body).filter(Boolean);
    return choiceLabels?.length ? choiceLabels.join(AnswerSeparator) : "-";
  }

  if (questionType === QuestionType.Checkbox) {
    if (answer.answer === "true") {
      return t("dashboard", "common.yes");
    }
    if (answer.answer === "false") {
      return t("dashboard", "common.no");
    }
    return "-";
  }

  if (questionType === QuestionType.Address) {
    const structuredAddress = parseStructuredAddress(answer.answer);
    if (structuredAddress) {
      return formatAddressForDisplay(structuredAddress);
    }
  }

  return answer.answer || "-";
};
